import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

export default function RequestAssignment() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [teams, setTeams] = useState([])
  const [groups, setGroups] = useState([])
  const [subgroups, setSubgroups] = useState([])
  const [selectedTeam, setSelectedTeam] = useState('')
  const [selectedGroup, setSelectedGroup] = useState('')
  const [selectedSubgroup, setSelectedSubgroup] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')
  const [user, setUser] = useState(null)

  useEffect(() => {
    loadUser()
    loadHierarchy()
  }, [])

  const loadUser = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser()
      
      const { data: userData } = await supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .single()

      setUser(userData)

      // Redirect if not unassigned
      if (userData.role !== 'unassigned') {
        navigate('/dashboard')
      }
    } catch (error) {
      console.error('Load user error:', error)
    }
  }

  const loadHierarchy = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser()
      
      const { data: userData } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', authUser.id)
        .single()

      // Load teams
      const { data: teamsData } = await supabase
        .from('groups')
        .select('*')
        .eq('org_id', userData.org_id)
        .eq('depth', 1)
        .order('name')

      // Load groups
      const { data: groupsData } = await supabase
        .from('groups')
        .select('*')
        .eq('org_id', userData.org_id)
        .eq('depth', 2)
        .order('name')

      // Load subgroups
      const { data: subgroupsData } = await supabase
        .from('groups')
        .select('*')
        .eq('org_id', userData.org_id)
        .eq('depth', 3)
        .order('name')

      setTeams(teamsData || [])
      setGroups(groupsData || [])
      setSubgroups(subgroupsData || [])
    } catch (error) {
      console.error('Load hierarchy error:', error)
    }
  }

  // Filter groups by selected team
  const filteredGroups = selectedTeam
    ? groups.filter(g => g.parent_id === selectedTeam)
    : []

  // Filter subgroups by selected group
  const filteredSubgroups = selectedGroup
    ? subgroups.filter(s => s.parent_id === selectedGroup)
    : []

 const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!selectedSubgroup) {
    alert('Please select a subgroup');
    return;
  }

  setSubmitting(true);

  try {
    // Get user's organization_id
    const { data: { user } } = await supabase.auth.getUser();
    
    const { data: userData } = await supabase
      .from('users')
      .select('org_id')
      .eq('id', user.id)
      .single();

    if (!userData?.org_id) {
      throw new Error('User organization not found');
    }

    // Insert assignment request with organization_id
    const { error } = await supabase
      .from('assignment_requests')
      .insert({
        user_id: userId,
        organization_id: userData.org_id,
        requested_subgroup_id: selectedSubgroup,
        status: 'pending'
      });

    if (error) throw error;

    alert('Request submitted successfully!');
    onSuccess?.();
    onClose();
  } catch (error) {
    console.error('Error submitting request:', error);
    alert('Failed to submit request. Please try again.');
  } finally {
    setSubmitting(false);
  }
};

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-xl font-bold text-gray-900">Request Assignment</h1>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 text-sm border rounded-md hover:bg-gray-50"
          >
            Back to Dashboard
          </button>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Request Group Assignment
          </h2>
          <p className="text-gray-600 mb-6">
            Select a subgroup you would like to join. Your request will be reviewed by a leader.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded text-sm">
                {error}
              </div>
            )}

            {/* Team Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Select Team *
              </label>
              <select
                value={selectedTeam}
                onChange={(e) => {
                  setSelectedTeam(e.target.value)
                  setSelectedGroup('')
                  setSelectedSubgroup('')
                }}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Choose team...</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </div>

            {/* Group Selection */}
            {selectedTeam && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Select Group *
                </label>
                <select
                  value={selectedGroup}
                  onChange={(e) => {
                    setSelectedGroup(e.target.value)
                    setSelectedSubgroup('')
                  }}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Choose group...</option>
                  {filteredGroups.map(group => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Subgroup Selection */}
            {selectedGroup && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Select Subgroup *
                </label>
                <select
                  value={selectedSubgroup}
                  onChange={(e) => setSelectedSubgroup(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Choose subgroup...</option>
                  {filteredSubgroups.map(subgroup => (
                    <option key={subgroup.id} value={subgroup.id}>{subgroup.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Reason (Optional) */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Reason (Optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Why do you want to join this subgroup?"
              />
            </div>

            <button
              type="submit"
              disabled={loading || !selectedSubgroup}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}